import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkle, ArrowClockwise, Copy, FilePdf } from "@phosphor-icons/react";

export default function ReportDialog({ open, onOpenChange, comparisonId, report, setReport }) {
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/comparisons/${comparisonId}/report`, { context });
      setReport(data.report);
    } catch (e) {
      toast.error("Report generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(report || "");
    toast.success("Report copied");
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/comparisons/${comparisonId}/report.pdf`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : "comparison_report.pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-primary" /> AI Comparison Report
          </DialogTitle>
          <DialogDescription className="sr-only">
            AI-generated narrative report of the dataset comparison results.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-border bg-secondary">
          <label className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Optional context for the analyst</label>
          <Textarea
            data-testid="report-context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. Dataset A is the 2023 baseline; Dataset B is the updated survey…"
            className="mt-2 bg-white h-20 resize-none"
          />
          <div className="mt-3 flex gap-2">
            <Button onClick={generate} disabled={busy} data-testid="run-report-button" className="gap-2">
              {report ? <ArrowClockwise size={16} /> : <Sparkle size={16} weight="fill" />}
              {busy ? "Analyzing…" : report ? "Regenerate" : "Generate report"}
            </Button>
            {report && (
              <Button variant="outline" onClick={copy} data-testid="copy-report-button" className="gap-2">
                <Copy size={16} /> Copy
              </Button>
            )}
            {report && (
              <Button
                variant="outline"
                onClick={exportPdf}
                disabled={exporting}
                data-testid="export-pdf-button"
                className="gap-2"
              >
                <FilePdf size={16} /> {exporting ? "Exporting…" : "Export PDF"}
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 py-5" data-testid="report-output">
          {busy && !report && (
            <div className="font-mono text-sm text-muted-foreground animate-pulse">
              Claude Sonnet 4.6 is analyzing the comparison…
            </div>
          )}
          {!busy && !report && (
            <div className="text-muted-foreground text-sm">
              Generate a narrative report with an executive summary, per-metric breakdown, and conclusions.
            </div>
          )}
          {report && (
            <article className="prose-report">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
            </article>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
